import { ANALYSIS_STEPS } from '@swg/domain';
import { theme } from '../theme.js';

/**
 * Horizontale stappenindicator bovenaan het analysescherm (conform prototype).
 * - Afgeronde stappen: groen met een vinkje.
 * - Huidige stap in het proces: blauw gevuld.
 * - Nog komende stappen: grijs.
 * De stap die je nu bekijkt krijgt een ring, zodat je bij het terugbladeren
 * naar een afgeronde stap toch ziet "waar je bent". Reeds bereikte stappen zijn
 * klikbaar om naartoe te navigeren.
 */
export function Stepper({
  current,
  viewStep,
  onNavigate,
}: {
  /** Verst bereikte stap (0–4), afgeleid van de runstatus. */
  current: number;
  /** Stap die nu getoond wordt. */
  viewStep: number;
  onNavigate: (index: number) => void;
}) {
  return (
    <div
      style={{
        background: theme.color.surface,
        border: `1px solid ${theme.color.border}`,
        borderRadius: 12,
        padding: '14px 18px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {ANALYSIS_STEPS.map((step, i) => {
          const done = i < current;
          const isCurrent = i === current;
          const reachable = i <= current;
          const viewed = i === viewStep;

          const bg = done ? theme.color.ok : isCurrent ? theme.color.primary : theme.color.surface;
          const fg = done || isCurrent ? '#FFFFFF' : theme.color.subtle;
          const border = done
            ? theme.color.ok
            : isCurrent
              ? theme.color.primary
              : theme.color.borderStrong;
          const labelColor = viewed
            ? theme.color.text
            : done
              ? theme.color.muted
              : isCurrent
                ? theme.color.text
                : theme.color.subtle;

          return (
            <div key={step.key} style={{ display: 'contents' }}>
              <button
                type="button"
                onClick={() => reachable && onNavigate(i)}
                disabled={!reachable}
                title={reachable ? `Ga naar: ${step.label}` : `${step.label} (nog niet bereikt)`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  cursor: reachable ? 'pointer' : 'default',
                  background: 'none',
                  border: 'none',
                  padding: '2px 2px',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    background: bg,
                    color: fg,
                    border: `1.5px solid ${border}`,
                    boxShadow: viewed ? `0 0 0 3px rgba(21,86,121,0.18)` : 'none',
                    flexShrink: 0,
                  }}
                >
                  {done ? '✓' : i + 1}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: viewed ? 600 : 500,
                    color: labelColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.label}
                </span>
              </button>
              {i < ANALYSIS_STEPS.length - 1 && (
                <span style={{ flex: 1, height: 1, background: theme.color.border, minWidth: 20 }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
